/* iter55z-o — HelpTip: unified tool-help popover.
 *
 * Behaviour (only when help mode is enabled in user prefs):
 *   • Hover  → 500ms delayed tooltip appears next to the element
 *   • Right-click → opens the same content in a larger, sticky panel
 *                    (auto-dismissed on outside click / Escape)
 *
 * Wrap any interactive element with <HelpTip content="…"> and the
 * child is passed through unchanged. Non-invasive: when help mode is
 * off, this component renders the child directly with no listeners.
 *
 * Enable / disable via the toggle in Settings → Preferences (persisted
 * in localStorage under the key `pw:help-mode`).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HelpCircle, PlayCircle } from 'lucide-react';
import { useFeatureVideoUrl } from '@/lib/useFeatureVideos';
import { VideoOverlay } from '@/components/VideoOverlay';

const STORAGE_KEY = 'pw:help-mode';

// External read/write helpers so non-React code (e.g. settings) can toggle.
export function readHelpMode() {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; }
  catch { return false; }
}
export function writeHelpMode(on) {
  try { localStorage.setItem(STORAGE_KEY, on ? '1' : '0'); }
  catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent('pw:help-mode-changed', { detail: !!on }));
}

// Hook: subscribes to global toggle changes.
export function useHelpMode() {
  const [on, setOn] = useState(readHelpMode);
  useEffect(() => {
    const h = (e) => setOn(!!e.detail);
    window.addEventListener('pw:help-mode-changed', h);
    return () => window.removeEventListener('pw:help-mode-changed', h);
  }, []);
  return on;
}

const HOVER_DELAY_MS = 500;

function computePos(rect, mode) {
  const w = 320;
  const gap = 8;
  let x = rect.left + rect.width / 2 - w / 2;
  let y = mode === 'hover' ? rect.top - 10 : rect.bottom + gap;
  x = Math.min(window.innerWidth - w - 12, Math.max(12, x));
  y = Math.max(12, y);
  return { x, y, w };
}

// iter-tip-refresh — Server-side tip cache shared across every mounted
// HelpTip. Loaded once per session; refreshes on window focus so a
// nightly cron rebuild propagates within one tab-switch.
let _tipCache = null;
let _tipLoading = null;
async function loadTips() {
  if (_tipCache) return _tipCache;
  if (_tipLoading) return _tipLoading;
  const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
  _tipLoading = fetch(`${API}/help/tips`, { credentials: 'include' })
    .then((r) => r.ok ? r.json() : { tips: {} })
    .then((d) => { _tipCache = d.tips || {}; return _tipCache; })
    .catch(() => ({}));
  return _tipLoading;
}
if (typeof window !== 'undefined') {
  window.addEventListener('focus', () => { _tipCache = null; _tipLoading = null; });
}

export function HelpTip({ title, content, children, side = 'bottom', slug = '' }) {
  const enabled = useHelpMode();
  const [tip, setTip]     = useState(null);   // {x,y,w,mode}
  const [sticky, setSticky] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [serverTip, setServerTip] = useState(null);
  useEffect(() => {
    if (!enabled || !slug) return;
    loadTips().then((tips) => {
      if (tips && tips[slug]) setServerTip(tips[slug]);
    });
  }, [enabled, slug]);
  const effectiveTitle   = serverTip?.title   || title;
  const effectiveContent = serverTip?.content || content;
  const hoverTimer = useRef(null);
  const ref = useRef(null);
  // iter-p2-d — pull the admin-bound video URL for this slug (if any).
  const videoUrl = useFeatureVideoUrl(slug);

  const showHover = useCallback((e) => {
    if (!enabled || sticky) return;
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;
      const pos = computePos(rect, 'hover');
      setTip({ ...pos, mode: 'hover' });
    }, HOVER_DELAY_MS);
  }, [enabled, sticky]);

  const hideHover = useCallback(() => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    if (!sticky) setTip(null);
  }, [sticky]);

  const openSticky = useCallback((e) => {
    if (!enabled) return;
    e.preventDefault();
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const pos = computePos(rect, 'sticky');
    setTip({ ...pos, mode: 'sticky' });
    setSticky(true);
  }, [enabled]);

  useEffect(() => {
    if (!sticky) return undefined;
    const onDoc = (e) => {
      if (!ref.current || !ref.current.contains(e.target)) {
        // If also outside the tip box, dismiss.
        const box = document.getElementById('help-tip-sticky');
        if (!box || !box.contains(e.target)) {
          setSticky(false); setTip(null);
        }
      }
    };
    const onEsc = (e) => { if (e.key === 'Escape') { setSticky(false); setTip(null); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [sticky]);

  if (!enabled) return children;

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={showHover}
        onMouseLeave={hideHover}
        onContextMenu={openSticky}
        className="contents"
      >
        {children}
      </span>
      {tip && (
        <div
          id={tip.mode === 'sticky' ? 'help-tip-sticky' : undefined}
          data-testid={tip.mode === 'sticky' ? 'help-tip-sticky' : 'help-tip-hover'}
          className={`fixed z-[70] rounded-md border shadow-xl ${
            tip.mode === 'sticky'
              ? 'border-primary/50 bg-card p-3'
              : 'border-border bg-card/95 p-2 backdrop-blur'
          }`}
          style={{ left: tip.x, top: tip.y - (tip.mode === 'hover' ? 8 : 0), width: tip.w }}
        >
          <div className="mb-1 flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] text-primary">
            <HelpCircle className="h-3 w-3" />
            {tip.mode === 'sticky' ? 'Help' : 'Hint'}
            {title && <span className="text-foreground/80 normal-case tracking-normal">· {title}</span>}
          </div>
          <div className={`${tip.mode === 'sticky' ? 'text-[12.5px]' : 'text-[11.5px]'} leading-relaxed text-foreground/90`}>
            {content}
          </div>
          {/* iter-p2-d — Watch button appears when an admin has bound a
              feature video to this slug. Only shown on the sticky
              (right-click) panel so hover tooltips stay lightweight. */}
          {tip.mode === 'sticky' && videoUrl && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPlaying(true); }}
              className="mt-2 inline-flex items-center gap-1 rounded-sm border border-primary/50 bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/20"
              data-testid={`help-tip-watch-${slug}`}
            >
              <PlayCircle className="h-3 w-3" />
              Watch (30&nbsp;sec)
            </button>
          )}
          {tip.mode === 'sticky' && (
            <div className="mt-2 text-right text-[10px] text-muted-foreground">
              Right-click a tool to pin. Press <kbd className="rounded-sm border border-border bg-secondary px-1 font-mono-stat">Esc</kbd> to close.
            </div>
          )}
        </div>
      )}
      {playing && (
        <VideoOverlay
          title={title || 'Feature explainer'}
          url={videoUrl}
          onClose={() => setPlaying(false)}
          testIdPrefix={`help-tip-video-${slug || 'x'}`}
        />
      )}
    </>
  );
}

export default HelpTip;
